/**
 * Poseidon hash over the BLS12-381 scalar field — isomorphic interface.
 *
 * Implementation strategy: wrap the circom-compiled `poseidonProbe*.wasm`
 * witness calculators. This makes the circuit ITSELF the source of truth —
 * any output @tonado/core produces is by construction what the
 * `--prime bls12381` circom Poseidon would produce.
 *
 * Two arities are supported (matching what circuits/commitmentHasher.circom
 * uses):
 *   - Poseidon(1) — for nullifierHash
 *   - Poseidon(2) — for commitment + merkle internal nodes
 *
 * Cost: ~10–30 ms per hash (wasm-bound). For our use cases — one Poseidon(2)
 * + one Poseidon(1) per deposit, ~21 Poseidon(2) for the empty-tree zeros
 * table, ~N Poseidon(2) per withdraw merkle rebuild — overhead is invisible.
 *
 * Artifact loading is injected. In Node, a default fs-backed loader is
 * lazily registered from `./poseidon-node-loader` so existing callers
 * (CLI, tests, smoke scripts) keep working with zero changes. In a browser,
 * the dApp must call `registerPoseidonArtifactLoader(...)` with a fetch- or
 * import-based loader BEFORE the first `buildPoseidon()` call.
 *
 * Driving the wasm witness calculator (rather than a hand-ported Poseidon
 * implementation) guarantees the hash output is bit-identical to what the
 * circuits themselves compute — the circuit is the source of truth.
 */
import { FIELD_SIZE_BLS12_381 } from "./constants";
let artifactLoader = null;
let cached = null;
/**
 * Register the artifact loader for Poseidon. Call once at app startup,
 * before any `buildPoseidon()` invocation. Re-calling invalidates the
 * cached hasher so the new loader takes effect on the next build.
 */
export function registerPoseidonArtifactLoader(loader) {
    artifactLoader = loader;
    cached = null;
}
/**
 * Build (and memoise) the Poseidon hasher. The first call resolves the
 * registered artifact loader; subsequent calls return the cached hasher.
 *
 * In Node, if no loader is registered we lazy-import the fs-backed default
 * from `./poseidon-node-loader`. In a browser this dynamic import fails
 * (no `node:fs`); the caller MUST register a loader first.
 */
export async function buildPoseidon() {
    if (cached)
        return cached;
    if (!artifactLoader) {
        try {
            // Magic comments tell Vite/webpack NOT to statically bundle this
            // import — `poseidon-node-loader.ts` pulls in `node:fs` which can't
            // run in the browser. The dApp registers its own loader BEFORE
            // calling buildPoseidon, so this fallback path never executes there.
            // Non-literal path defeats TS's static include of the file and
            // keeps the ESM build (which excludes src/node/**) from referencing
            // a non-existent module. Browser bundlers also skip the resolve.
            // .js extension required by Node ESM strict-resolution (vitest /
            // raw `node` consumers). The `@vite-ignore` keeps browser bundlers
            // from tracing it; the non-literal-prefixed variable defeats TS's
            // static include since the source path has no extension.
            const nodeLoaderPath = "./node/poseidon-loader.js";
            const m = (await import(
            /* @vite-ignore */ /* webpackIgnore: true */ nodeLoaderPath));
            m.registerNodePoseidonDefaults();
        }
        catch (e) {
            throw new Error("No Poseidon artifact loader registered. In a browser/Worker, call " +
                "`registerPoseidonArtifactLoader(...)` with a fetch- or import-based " +
                "loader BEFORE the first `buildPoseidon()` call. " +
                `(Underlying error trying to lazy-load Node defaults: ${e.message})`);
        }
    }
    const a1 = await artifactLoader("poseidonProbe1");
    const a2 = await artifactLoader("poseidonProbe");
    const calc1 = await a1.buildCalculator(a1.wasm);
    const calc2 = await a2.buildCalculator(a2.wasm);
    const normalize = (out) => {
        const v = BigInt(out);
        return ((v % FIELD_SIZE_BLS12_381) + FIELD_SIZE_BLS12_381) % FIELD_SIZE_BLS12_381;
    };
    cached = async (inputs) => {
        const calc = inputs.length === 1 ? calc1 : inputs.length === 2 ? calc2 : null;
        if (!calc) {
            throw new Error(`BLS12-381 Poseidon supports input lengths 1 or 2; got ${inputs.length}.`);
        }
        const witness = await calc.calculateWitness({ inputs: inputs.map((x) => BigInt(x)) }, false);
        // Probe circuit layouts (both variants):
        //   witness[0] = 1 (constant)
        //   witness[1] = hash output
        //   witness[2..] = inputs + internal signals
        return normalize(witness[1]);
    };
    return cached;
}
/**
 * Invalidate the cached hasher. Useful for tests that want to re-register
 * a different loader between cases.
 */
export function resetPoseidonCache() {
    cached = null;
}
//# sourceMappingURL=poseidon.js.map